import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Copy, ExternalLink, CheckCircle2, AlertTriangle, Stethoscope, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CopyField = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Clipboard blocked");
    }
  };
  return (
    <div className="mb-3">
      <div className="text-[11px] uppercase tracking-wider text-ink-muted mb-1">{label}</div>
      <div className="flex items-stretch gap-2">
        <div
          className="flex-1 rounded-md border border-line bg-bg-elevated px-3 py-2 font-mono text-xs break-all"
          data-testid={`${testid}-value`}
        >
          {value || <span className="text-ink-muted italic">not configured</span>}
        </div>
        <button
          type="button"
          onClick={copy}
          disabled={!value}
          data-testid={`${testid}-copy-btn`}
          className="rounded-md border border-line bg-bg-elevated px-3 text-xs hover:border-brand-blue transition flex items-center gap-1 disabled:opacity-40"
        >
          {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
};

const ProviderBlock = ({ title, accent, info, dashboardLinks, instructions }) => {
  return (
    <div className="rounded-xl border border-line bg-bg-elevated p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-bold" style={{ color: accent }}>{title}</div>
        <div className="flex gap-2">
          {dashboardLinks.map((d) => (
            <a
              key={d.url}
              href={d.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`setup-link-${d.testid}`}
              className="text-[11px] inline-flex items-center gap-1 rounded-md border border-line bg-bg-surface px-2 py-1 hover:border-brand-blue transition"
            >
              {d.label} <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      </div>
      {info.map((f) => (
        <CopyField key={f.testid} label={f.label} value={f.value} testid={f.testid} />
      ))}
      {instructions && (
        <ol className="mt-2 space-y-1 text-xs text-ink-secondary list-decimal list-inside">
          {instructions.map((line, i) => <li key={i}>{line}</li>)}
        </ol>
      )}
    </div>
  );
};

export default function SetupHelper() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  useEffect(() => {
    if (!open || info) return;
    api.get("/social/setup-info").then((r) => setInfo(r.data)).catch(() => {
      toast.error("Could not load setup info");
    });
  }, [open, info]);

  const runDiagnosis = async () => {
    setDiagLoading(true);
    setDiag(null);
    try {
      const r = await api.get("/social/diagnose/meta");
      setDiag(r.data);
      if (r.data?.findings?.some((f) => f.level === "error")) {
        toast.error("Found issues with your Meta app config — see details below.", { duration: 7000 });
      } else if (r.data?.findings?.some((f) => f.level === "warn")) {
        toast.warning("Meta app looks mostly OK with some warnings — see details below.", { duration: 7000 });
      } else {
        toast.success("Meta app config looks healthy.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnostic failed");
    } finally {
      setDiagLoading(false);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-brand-blue/40 bg-brand-blue/5" data-testid="setup-helper">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="setup-helper-toggle"
        className="w-full flex items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-brand-blue" />
          <div>
            <div className="font-display font-bold">Getting a "URL Blocked" error? Open this.</div>
            <div className="text-xs text-ink-muted">
              The exact values to paste into Meta / X / TikTok / Google developer dashboards — copy with one click.
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-ink-muted" /> : <ChevronDown className="h-5 w-5 text-ink-muted" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          {!info ? (
            <div className="text-xs text-ink-muted font-mono">Loading…</div>
          ) : (
            <>
              <div className="rounded-md border border-line bg-bg-elevated p-3 text-xs">
                <div className="font-semibold mb-1">Universal values</div>
                <CopyField label="App Domain (no protocol)" value={info.app_domain} testid="setup-app-domain" />
                <CopyField label="Privacy Policy URL" value={info.privacy_policy_url} testid="setup-privacy" />
                <CopyField label="Terms of Service URL" value={info.terms_url} testid="setup-terms" />
              </div>

              <ProviderBlock
                title="Meta (Facebook + Instagram)"
                accent="#1877F2"
                info={[
                  { label: "App ID", value: info.meta.app_id, testid: "setup-meta-appid" },
                  { label: "Valid OAuth Redirect URI", value: info.meta.redirect_uri, testid: "setup-meta-redirect" },
                  { label: "Required scopes", value: info.meta.scopes, testid: "setup-meta-scopes" },
                ]}
                dashboardLinks={[
                  { label: "Facebook Login Settings", url: info.meta.dashboard_url, testid: "meta-fb-login" },
                  { label: "App Settings → Basic", url: info.meta.settings_basic_url, testid: "meta-basic" },
                  { label: "App Roles", url: info.meta.roles_url, testid: "meta-roles" },
                  { label: "Add Product (Facebook Login)", url: info.meta.add_product_url, testid: "meta-add-product" },
                ]}
                instructions={[
                  'FIRST: In the App dashboard left sidebar click "+ Add Product" and ADD the "Facebook Login → Web" product. Without it, the "Valid OAuth Redirect URIs" field does not exist and Facebook always says "URL Blocked".',
                  'Then in "Facebook Login → Settings": turn ON Client OAuth Login AND Web OAuth Login, paste the Redirect URI above into "Valid OAuth Redirect URIs", and click Save Changes at the bottom.',
                  'In "Settings → Basic": paste the App Domain above and the Privacy Policy URL, then Save.',
                  'In "App Roles": add yourself as a Tester (or Admin) so the app accepts your login while in Development mode.',
                  "Wait ~30s for Meta to propagate changes, then click 'Run Live Diagnosis' below to verify, then click Connect on the Facebook Page card above.",
                ]}
              />

              <div className="rounded-xl border border-line bg-bg-elevated p-4" data-testid="meta-diagnostic-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display font-bold text-brand-blue flex items-center gap-2">
                    <Stethoscope className="h-5 w-5" /> Live Meta App Diagnosis
                  </div>
                  <button
                    type="button"
                    onClick={runDiagnosis}
                    disabled={diagLoading}
                    data-testid="meta-diagnose-btn"
                    className="text-xs inline-flex items-center gap-1 rounded-md border border-brand-blue/50 bg-brand-blue/10 text-brand-blue px-3 py-1.5 hover:bg-brand-blue/20 transition disabled:opacity-50"
                  >
                    {diagLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Stethoscope className="h-3.5 w-3.5" />}
                    {diagLoading ? "Diagnosing…" : "Run Live Diagnosis"}
                  </button>
                </div>
                <div className="text-xs text-ink-secondary mb-3">
                  We'll query Facebook's Graph API with your server-side App Secret and report back what's actually configured (App Domains, Privacy Policy URL, products added). This catches the most common "URL Blocked" root causes.
                </div>
                {diag && (
                  <div className="space-y-2" data-testid="meta-diag-results">
                    {(diag.findings || []).map((f, i) => {
                      const Icon = f.level === "ok" ? CheckCircle2 : f.level === "error" ? XCircle : AlertTriangle;
                      const color = f.level === "ok" ? "text-emerald-300" : f.level === "error" ? "text-brand-onair" : "text-brand-amber";
                      return (
                        <div key={i} className="flex items-start gap-2 text-xs" data-testid={`diag-finding-${f.level}`}>
                          <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${color}`} />
                          <span className={color}>{f.msg}</span>
                        </div>
                      );
                    })}
                    {diag.graph_app_info && Object.keys(diag.graph_app_info).length > 0 && (
                      <details className="mt-3 text-xs text-ink-muted">
                        <summary className="cursor-pointer hover:text-ink-secondary">Raw Graph API response</summary>
                        <pre className="mt-2 p-2 rounded bg-bg-elevated border border-line overflow-auto text-[11px] font-mono">{JSON.stringify(diag.graph_app_info, null, 2)}</pre>
                      </details>
                    )}
                  </div>
                )}
              </div>

              <ProviderBlock
                title="Threads (Meta)"
                accent="#F5F1EA"
                info={[
                  { label: "App ID", value: info.threads.app_id, testid: "setup-threads-appid" },
                  { label: "Redirect Callback URL", value: info.threads.redirect_uri, testid: "setup-threads-redirect" },
                  { label: "Required scopes", value: info.threads.scopes, testid: "setup-threads-scopes" },
                ]}
                dashboardLinks={[
                  { label: "Use Cases / Permissions", url: info.threads.dashboard_url, testid: "threads-usecases" },
                  { label: "Roles → Threads Tester", url: info.threads.roles_url, testid: "threads-roles" },
                ]}
                instructions={[
                  'Open "Use Cases" → click "Customize" on "Access the Threads API".',
                  "Toggle ON: threads_basic and threads_content_publish.",
                  'In the same Use Case Settings panel, find "Redirect Callback URLs" and paste the value above, then Save.',
                  'In "Roles → Threads Tester", invite your Threads handle and accept the invite from Threads.net → Settings → Account → Website Permissions.',
                ]}
              />

              <ProviderBlock
                title="X (Twitter)"
                accent="#0EA5E9"
                info={[
                  { label: "Client ID", value: info.x.client_id, testid: "setup-x-clientid" },
                  { label: "Callback URI", value: info.x.redirect_uri, testid: "setup-x-redirect" },
                  { label: "Required scopes", value: info.x.scopes, testid: "setup-x-scopes" },
                ]}
                dashboardLinks={[{ label: "X Developer Portal", url: info.x.dashboard_url, testid: "x-portal" }]}
                instructions={[
                  'Create a Project + App with OAuth 2.0 enabled (User authentication settings → "Set up").',
                  'Type of App: "Web App". Paste the Callback URI above.',
                  "Save your Client ID + Secret and send them to the admin to add to .env (X_CLIENT_ID / X_CLIENT_SECRET).",
                ]}
              />

              <ProviderBlock
                title="TikTok"
                accent="#FF0050"
                info={[
                  { label: "Client Key", value: info.tiktok.client_key, testid: "setup-tiktok-key" },
                  { label: "Redirect URI", value: info.tiktok.redirect_uri, testid: "setup-tiktok-redirect" },
                  { label: "Required scopes", value: info.tiktok.scopes, testid: "setup-tiktok-scopes" },
                ]}
                dashboardLinks={[{ label: "TikTok Developer Apps", url: info.tiktok.dashboard_url, testid: "tiktok-apps" }]}
                instructions={[
                  "Create an App → Login Kit + Content Posting API.",
                  "Add the Redirect URI exactly as shown.",
                  "Submit for audit if you want to publish on behalf of any TikTok account (not just sandbox).",
                ]}
              />

              <ProviderBlock
                title="YouTube (Google)"
                accent="#FF0000"
                info={[
                  { label: "Client ID", value: info.youtube.client_id, testid: "setup-yt-clientid" },
                  { label: "Authorized redirect URI", value: info.youtube.redirect_uri, testid: "setup-yt-redirect" },
                  { label: "Required scope", value: info.youtube.scopes, testid: "setup-yt-scopes" },
                ]}
                dashboardLinks={[{ label: "Google Cloud Credentials", url: info.youtube.dashboard_url, testid: "google-creds" }]}
                instructions={[
                  "Create an OAuth Client ID (Web application) in Google Cloud Console.",
                  "Paste the Authorized redirect URI above. Save.",
                  "Enable the YouTube Data API v3 on the same project.",
                  "Send the Client ID + Secret to the admin to add to .env (GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET).",
                ]}
              />

              {info.linkedin && (
                <ProviderBlock
                  title="LinkedIn"
                  accent="#0A66C2"
                  info={[
                    { label: "Client ID", value: info.linkedin.client_id, testid: "setup-li-clientid" },
                    { label: "Authorized redirect URL", value: info.linkedin.redirect_uri, testid: "setup-li-redirect" },
                    { label: "Required scopes", value: info.linkedin.scopes, testid: "setup-li-scopes" },
                  ]}
                  dashboardLinks={[{ label: "LinkedIn Developer Apps", url: info.linkedin.dashboard_url, testid: "linkedin-apps" }]}
                  instructions={[
                    "Create an app at linkedin.com/developers (needs a LinkedIn Page to attach it to).",
                    "Under Products, add \"Share on LinkedIn\" and \"Sign In with LinkedIn using OpenID Connect\".",
                    "Auth tab → paste the Authorized redirect URL above. Save.",
                    "Send the Client ID + Secret to the admin to add to .env (LINKEDIN_CLIENT_ID / LINKEDIN_CLIENT_SECRET).",
                  ]}
                />
              )}
              {info.pinterest && (
                <ProviderBlock
                  title="Pinterest"
                  accent="#E60023"
                  info={[
                    { label: "App ID", value: info.pinterest.app_id, testid: "setup-pin-appid" },
                    { label: "Redirect URI", value: info.pinterest.redirect_uri, testid: "setup-pin-redirect" },
                    { label: "Required scopes", value: info.pinterest.scopes, testid: "setup-pin-scopes" },
                  ]}
                  dashboardLinks={[{ label: "Pinterest Developer Apps", url: info.pinterest.dashboard_url, testid: "pinterest-apps" }]}
                  instructions={[
                    "Create an app at developers.pinterest.com/apps (requires a Pinterest business account).",
                    "Paste the Redirect URI above into the app's Redirect URIs. Save.",
                    "New apps start in Trial access — Pins are only visible to you until Pinterest grants Standard access.",
                    "Send the App ID + Secret to the admin to add to .env (PINTEREST_APP_ID / PINTEREST_APP_SECRET).",
                  ]}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
